import { useState, useEffect } from "react";
import {
  Modal,
  Button,
  Group,
  Stack,
  Text,
  Checkbox,
  Loader,
} from "@mantine/core";
import { IconFileInvoice } from "@tabler/icons-react";
import { useCustomerPaymentsQuery } from "../api/queries/customerRepresentativeQueries";
import { displayCurrencyAmount, displayISODate } from "../utils/display";
import type { Payment } from "../utils/interfaces";

interface ChooseInvoicesModalProps {
  opened: boolean;
  onClose: () => void;
  customerRepId: number | undefined;
  onAddInvoices: (externalIds: string[]) => Promise<void>;
  isLoading: boolean;
  currentInvoiceId?: string | null;
}

export const ChooseInvoicesModal = ({
  opened,
  onClose,
  customerRepId,
  onAddInvoices,
  isLoading,
  currentInvoiceId,
}: ChooseInvoicesModalProps) => {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const { data: payments = [], isLoading: isPaymentsLoading } =
    useCustomerPaymentsQuery(customerRepId, {
      enabled: Boolean(customerRepId) && opened,
      staleTime: undefined,
      gcTime: undefined,
    });

  useEffect(() => {
    if (!opened) {
      setSelectedIds(new Set());
    } else if (currentInvoiceId) {
      setSelectedIds((prev) => new Set(prev).add(currentInvoiceId));
    }
  }, [opened, currentInvoiceId]);

  const togglePayment = (externalId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(externalId)) {
        next.delete(externalId);
      } else {
        next.add(externalId);
      }
      return next;
    });
  };

  const handleAddSelected = async () => {
    const ids: string[] = Array.from(selectedIds);
    if (ids.length === 0) return;
    await onAddInvoices(ids);
    onClose();
  };

  const handleClose = () => {
    onClose();
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      centered
      title={
        <Group gap="xs">
          <IconFileInvoice size={20} />
          <Text size="lg" fw={600}>
            Choose invoices to attach
          </Text>
        </Group>
      }
      radius="md"
      padding="xl"
      styles={{
        content: {
          boxShadow:
            "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
        },
      }}
    >
      <Stack gap="md">
        {isPaymentsLoading ? (
          <Group justify="center" py="xl">
            <Loader size="sm" />
            <Text size="sm" c="dimmed">
              Loading invoices…
            </Text>
          </Group>
        ) : payments.length === 0 ? (
          <Text size="sm" c="dimmed">
            No invoices found for this customer.
          </Text>
        ) : (
          <div className="max-h-64 overflow-y-auto space-y-2 pr-1">
            {payments.map((payment: Payment) => (
              <Checkbox
                key={payment.id}
                label={
                  <div className="flex flex-col gap-0.5">
                    <Text size="sm" fw={500}>
                      {payment.external_id}
                    </Text>
                    <Text size="xs" c="dimmed">
                      {displayISODate(payment.due_date)} ·{" "}
                      {displayCurrencyAmount(
                        payment.amount,
                        payment.currency_type,
                      )}
                    </Text>
                  </div>
                }
                checked={selectedIds.has(payment.external_id)}
                onChange={() => togglePayment(payment.external_id)}
                radius="sm"
              />
            ))}
          </div>
        )}

        <Group justify="flex-end" mt="lg">
          <Button
            variant="subtle"
            color="gray"
            radius="md"
            onClick={handleClose}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleAddSelected}
            loading={isLoading}
            disabled={
              isPaymentsLoading ||
              payments.length === 0 ||
              selectedIds.size === 0
            }
            color="green"
            radius="md"
            leftSection={<IconFileInvoice size={16} />}
          >
            Add selected
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};
